import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarRef as MatSnackBarRef,
  LegacyTextOnlySnackBar as TextOnlySnackBar,
} from '@angular/material/legacy-snack-bar';

import { finalize, map, Observable, of, Subject, switchMap, tap, timer } from 'rxjs';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ImportsService } from './imports.service';
import { AccountService, PusherService } from 'app/core/services';
import { ImportDialogComponent } from './components/import-dialog/import-dialog.component';
import { FieldsTemplateDialogComponent } from './components/fields-template-dialog/fields-template-dialog.component';
import { OutputImportDialogData, ImportStatusEnum, ImportResponse, DocumentDetails } from './imports.types';

@Component({
  selector: 'imports-settings',
  templateUrl: './imports.component.html',
  styleUrls: ['./imports.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportsSettingsComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  public importListResults$: Observable<ImportResponse[]>;
  public currentPage: number = 1;
  public pageSize: number = 20;
  public totalCount: number = 0;
  public getImportListError$: Subject<HttpErrorResponse>;
  public runImportError: HttpErrorResponse;
  public selectedImport: ImportResponse;
  public importStatus = ImportStatusEnum;
  public importRecordsRunState: boolean[] = [];

  @ViewChildren('importPanel') importPanels!: QueryList<MatExpansionPanel>;

  private get _accountId(): number {
    return Number(this._accountService.getCurrentAccount().id);
  }

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _snackBar: MatSnackBar,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _importsService: ImportsService,
    private readonly _accountService: AccountService,
    private readonly _pusherService: PusherService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchImportsList$();
  }

  ngOnDestroy(): void {
    this.destroyImportChannels();
    super.ngOnDestroy();
  }

  private destroyImportChannels(): void {
    this.importListResults$.pipe(untilComponentDestroyed(this)).subscribe((importResults: ImportResponse[]) => {
      importResults.forEach((importResult: ImportResponse) => {
        const channelName = `Import-${importResult?.id}`;
        this._pusherService.closeChannel(channelName);
      });
    });
  }

  public fetchImportsList$(): void {
    this.getImportListError$ = this._importsService.getErrorSubject$;
    this.importListResults$ = this._importsService.get$(this._accountId, this.currentPage, this.pageSize).pipe(
      tap((response) => {
        this.totalCount = response.total;
      }),
      map((response) => response.imports),
      untilComponentDestroyed(this),
    );
  }

  // Use it to display associated headers/columns summary to the user based on import status.
  public enableMappingSummaryView(importStatus: string): boolean {
    switch (importStatus) {
      case this.importStatus.Imported:
      case this.importStatus.InRunQueue:
      case this.importStatus.Invalid:
      case this.importStatus.ReadyToRun:
      case this.importStatus.Running:
      case this.importStatus.ImportedWithFailures:
        return true;
      default:
        return false;
    }
  }

  public enableEditImportButton(importStatus: string): boolean {
    switch (importStatus) {
      case this.importStatus.Invalid:
      case this.importStatus.MappingRequired:
      case this.importStatus.ReadyToRun:
      case this.importStatus.New:
        return true;
      default:
        return false;
    }
  }

  public enableDownloadButton(importRecord: ImportResponse): boolean {
    return importRecord.document ? true : false;
  }

  public enableRunButton(importStatus: string): boolean {
    switch (importStatus) {
      case this.importStatus.ReadyToRun:
        return true;
      default:
        return false;
    }
  }

  public applyStatusColor(importStatus: string): string {
    switch (importStatus) {
      // warning
      case this.importStatus.InRunQueue:
      case this.importStatus.MappingRequired:
      case this.importStatus.Running:
        return '#ff6700';

      // error
      case this.importStatus.Invalid:
      case this.importStatus.ImportedWithFailures:
        return '#f05252';

      // success
      case this.importStatus.Imported:
      case this.importStatus.New:
      case this.importStatus.ReadyToRun:
        return '#0e9f6e';
    }
  }

  public openDownloadTemplateDialog(): void {
    this._matDialog
      .open(FieldsTemplateDialogComponent, {
        panelClass: 'export-fields-dialog',
        data: { accountId: this._accountId },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((res: { finished: boolean }) => {
        if (res) this.triggerSnackBar('Your download should begin shortly...');
      });
  }

  public openImportStepperDialog(importRecord?: ImportResponse): void {
    this._matDialog
      .open(ImportDialogComponent, {
        panelClass: 'full-screen-dialog',
        disableClose: true,
        data: { import: importRecord, accountId: this._accountId, editMode: importRecord ? true : false },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((response: OutputImportDialogData) => {
        this.currentPage = 1;
        this.getImportsList$(response);
        // Clear run import errors...
        this.runImportError = null;
        this._cdRef.detectChanges();
      });
  }

  private getImportsList$(response: OutputImportDialogData): void {
    this.importListResults$ = this._importsService.get$(this._accountId, this.currentPage, this.pageSize).pipe(
      tap((result) => {
        this.totalCount = result.total; // update total count for paginator
        if (response) {
          const newImportIndex = result.imports.findIndex((item) => item.id === response.recordId);
          this.triggerSnackBar('Successfully created import');
          if (newImportIndex !== -1) this.expandLastTouchedImport(newImportIndex);
        }
      }),
      map((result) => result.imports),
    );
  }

  /**
   * Expand last edited or newly created import panel.
   * @param importIndex index of the import panel to expand
   * @param delay time in milliseconds to wait before expanding the panel. Default is 1000ms.
   */
  private expandLastTouchedImport(importIndex: number, delay: number = 1000): void {
    timer(delay)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        if (this.importPanels.length) this.importPanels.toArray()[importIndex].open();
      });
  }

  public deleteImport$(importRecord: ImportResponse): void {
    this._importsService
      .delete$(this._accountId, importRecord.id)
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() => {
          return this._importsService.get$(this._accountId).pipe(untilComponentDestroyed(this));
        }),
      )
      .subscribe({
        next: (updatedImports: { imports: ImportResponse[]; total: number }) => {
          this.importListResults$ = of(updatedImports.imports);
          this.totalCount = updatedImports.total;
          this._cdRef.detectChanges();
          this.triggerSnackBar('Successfully deleted import');
        },
        error: () => this.triggerSnackBar('Unable to delete selected import. Try again later.'),
      });
  }

  public downloadFile(attachment: DocumentDetails): void {
    const link = document.createElement('a');
    link.href = attachment.original_url;
    link.download = attachment.public_name;
    link.target = '_self';
    link.click();
    link.remove();
  }

  public runImport(importRecord: ImportResponse, index: number): void {
    this.runProcessStart(index);
    this.selectedImport = importRecord;

    this.runImportProcess(importRecord).subscribe((response: ImportStatusEnum) =>
      this.handleImportStatusResponse(response, index),
    );
  }

  public onPageChange(event: { activePage: number; pageSize: number }): void {
    // event.activePage is 1-based (starts from 1)
    this.currentPage = event.activePage;
    this.pageSize = event.pageSize;

    // Fetch the new page of data
    this.fetchImportsList$();

    // Reset expansion panels state
    if (this.importPanels) {
      this.importPanels.forEach((panel) => panel.close());
    }

    // Optional: Scroll to top of the list
    window.scrollTo(0, 0);
  }

  /**
   * Handles the import running process, import channel creation and updates.
   * @param importRecord ImportResponse
   * @returns
   */
  private runImportProcess(importRecord: ImportResponse): Observable<ImportStatusEnum> {
    const runImport$ = this._importsService.run$(this._accountId, importRecord.id);

    const updateChannel$ = runImport$.pipe(
      untilComponentDestroyed(this),
      switchMap(() => {
        const channelName = `Import-${importRecord.id}`;
        return this._pusherService.listenToChannel({ channelName, eventName: 'update' }).pipe(
          untilComponentDestroyed(this),
          map(({ status }) => status),
        );
      }),
    );

    return updateChannel$;
  }

  /**
   * Update import record status and update entire import data if status is Imported or ImportedWithFailures.
   * @param response ImportStatusEnum
   */
  private handleImportStatusResponse(response: ImportStatusEnum, index: number): void {
    this.selectedImport.status = response;
    this._cdRef.detectChanges();

    if (
      this.selectedImport.status === this.importStatus.ImportedWithFailures ||
      this.selectedImport.status === this.importStatus.Imported
    ) {
      this.updateImportAndList(index);
    }
  }

  /**
   * Fetch new import data based on id and update the import list.
   */
  private updateImportAndList(index: number): void {
    this._importsService.getImportById$(this._accountId, this.selectedImport.id).subscribe((importResponse) => {
      Object.assign(this.selectedImport, importResponse);

      this.importListResults$.subscribe((importList) => {
        const listIndex = importList.findIndex((item) => item.id === importResponse.id);
        if (listIndex !== -1) {
          Object.assign(importList[listIndex], importResponse);
          this.runProcessEnd(index);
          this._cdRef.detectChanges();
        }
      });
    });
  }

  /**
   * Sets loading state to true to display the mat-spinner loader.
   * @param index index of the import record in the list.
   */
  private runProcessStart(index: number): void {
    this.importRecordsRunState[index] = true;
  }

  /**
   * Sets loading state to false to hide the mat-spinner loader.
   * @param index index of the import record in the list.
   */
  private runProcessEnd(index: number): void {
    this.importRecordsRunState[index] = false;
  }

  private triggerSnackBar(message: string): MatSnackBarRef<TextOnlySnackBar> {
    return this._snackBar.open(`${message}`, null, {
      duration: 3000,
    });
  }
}
